import React, { useState, useEffect } from "react";
import { Search, Plus, Edit, Eye, AlertTriangle, Calendar, X, Download, AlertCircle, Truck } from "lucide-react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import * as roldanApi from "../../../../../../ApiCalls/taktikApiCalls/roldanApi";
import { formatDateOnlyForDisplay, getColombiaDateString, isFechaVencidaColombia } from "../../../../../../utils/dateUtils";

const RemesaList = ({
  mantisBusinessId,
  mantisBusinessUserId,
  token,
  onRemesaSelect,
  onCreateRemesa,
  onEditRemesa,
  onPreManifiestoSelect,
  refreshTrigger
}) => {
  const [remesas, setRemesas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingPreManifiestoForRemesaId, setCreatingPreManifiestoForRemesaId] = useState(null);
  const [filters, setFilters] = useState({
    estado: null,
    estado_asociacion: null,
    search: "",
    fecha_desde: null,
    fecha_hasta: null
  });

  useEffect(() => {
    loadRemesas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mantisBusinessId, refreshTrigger, filters]);

  const loadRemesas = async () => {
    setLoading(true);
    try {
      // Build filter object, only including date filters when both dates are valid
      const apiFilters = {
        limit: 100,
        ...(filters.estado && {estado: filters.estado}),
        ...(filters.estado_asociacion && {
          estado_asociacion: filters.estado_asociacion
        })
      };

      // Only apply date filters if both dates are set and valid
      if (filters.fecha_desde && filters.fecha_hasta) {
        apiFilters.fecha_desde = filters.fecha_desde;
        apiFilters.fecha_hasta = filters.fecha_hasta;
      }

      const res = await roldanApi.getRemesas(
        mantisBusinessId,
        token,
        apiFilters
      );
      if (res?.success) {
        let remesasData = res.data || [];

        // Apply client-side search filter if provided
        if (filters.search) {
          remesasData = remesasData.filter(
            (r) =>
              r.numero_remesa
                .toLowerCase()
                .includes(filters.search.toLowerCase()) ||
              r.cliente?.toLowerCase().includes(filters.search.toLowerCase()) ||
              r.nit_cliente?.toLowerCase().includes(filters.search.toLowerCase()) ||
              r.nombre_conductor
                ?.toLowerCase()
                .includes(filters.search.toLowerCase())
          );
        }

        setRemesas(remesasData);
      }
    } catch (error) {
      console.error("Error loading remesas:", error);
    } finally {
      setLoading(false);
    }
  };

  const buildPreManifiestoDataFromRemesa = (remesa) => {
    const fechaCargue = remesa.fecha_cargue || remesa.fecha_creacion;
    const fechaViaje = fechaCargue
      ? new Date(fechaCargue).toISOString().split("T")[0]
      : getColombiaDateString();
    return {
      fecha_viaje: fechaViaje,
      origen: remesa.origen || "",
      origen_id: remesa.origen_id || "",
      destino: remesa.destino || "",
      destino_id: remesa.destino_id || "",
      estado: "Abierto",
    };
  };

  const handleCreatePreManifiestoFromRemesa = async (remesa, e) => {
    e?.stopPropagation?.();
    if (!remesa?.remesa_id || !onPreManifiestoSelect) return;
    setCreatingPreManifiestoForRemesaId(remesa.remesa_id);
    try {
      const preManifiestoData = buildPreManifiestoDataFromRemesa(remesa);
      const createRes = await roldanApi.createPreManifiesto(
        mantisBusinessId,
        mantisBusinessUserId,
        token,
        preManifiestoData
      );
      if (!createRes?.success || !createRes?.data?.pre_manifiesto_id) {
        toast.error(createRes?.message || "Error al crear el pre-manifiesto");
        return;
      }
      const preManifiestoId = createRes.data.pre_manifiesto_id;
      const assignRes = await roldanApi.assignRemesasToPreManifiesto(
        mantisBusinessId,
        token,
        preManifiestoId,
        [remesa.remesa_id]
      );
      if (!assignRes?.success) {
        toast.error(assignRes?.message || "Error al asignar la remesa al pre-manifiesto");
        return;
      }
      toast.success(`Pre-manifiesto creado y remesa ${remesa.numero_remesa} asignada`);
      loadRemesas();
      onPreManifiestoSelect(preManifiestoId);
    } catch (err) {
      console.error("Error creating pre-manifiesto from remesa:", err);
      toast.error("Error al crear el pre-manifiesto");
    } finally {
      setCreatingPreManifiestoForRemesaId(null);
    }
  };

  const exportToExcel = () => {
    try {
      // Prepare data for export
      const exportData = remesas.map((remesa) => ({
        "N° Remesa": remesa.numero_remesa,
        Cliente: remesa.cliente || "N/A",
        "NIT Cliente": remesa.nit_cliente || "N/A",
        "Dirección Cliente": remesa.direccion_cliente || "N/A",
        "Código Ciudad Cliente": remesa.codigo_ciudad_cliente || "N/A",
        Conductor: remesa.nombre_conductor || "N/A",
        "CC Conductor": remesa.cedula_conductor || "N/A",
        Placa: remesa.placa_vehiculo || "N/A",
        Celular: remesa.celular || "N/A",
        Origen: remesa.origen || "N/A",
        Destino: remesa.destino || "N/A",
        Estado: remesa.estado || "N/A",
        "Fecha Creación": remesa.fecha_creacion
          ? new Date(remesa.fecha_creacion).toLocaleDateString("es-ES")
          : "N/A",
        "Fecha Cargue": remesa.fecha_cargue
          ? new Date(remesa.fecha_cargue).toLocaleDateString("es-ES")
          : "N/A",
        "Hora Cargue": remesa.hora_cargue || "N/A",
        "Peso (kg)": remesa.peso_mercancia_kg || "N/A",
        "Tipo Mercancía": remesa.tipo_mercancia || "N/A",
        Flete: remesa.flete ? parseFloat(remesa.flete).toLocaleString() : "N/A",
        Anticipo: remesa.anticipo
          ? parseFloat(remesa.anticipo).toLocaleString()
          : "N/A",
        "Pre-Manifiesto": remesa.numero_pre_manifiesto || "N/A",
        "Teléfono Creador": remesa.creado_por_telefono || "N/A"
      }));

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      ws["!cols"] = [
        {wch: 15}, // N° Remesa
        {wch: 25}, // Cliente
        {wch: 15}, // NIT Cliente
        {wch: 30}, // Dirección Cliente
        {wch: 20}, // Código Ciudad Cliente
        {wch: 25}, // Conductor
        {wch: 15}, // CC Conductor
        {wch: 12}, // Placa
        {wch: 15}, // Celular
        {wch: 20}, // Origen
        {wch: 20}, // Destino
        {wch: 20}, // Estado
        {wch: 15}, // Fecha Creación
        {wch: 15}, // Fecha Cargue
        {wch: 12}, // Hora Cargue
        {wch: 12}, // Peso
        {wch: 25}, // Tipo Mercancía
        {wch: 15}, // Flete
        {wch: 15}, // Anticipo
        {wch: 20}, // Pre-Manifiesto
        {wch: 18} // Teléfono Creador
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Remesas");

      // Generate filename with date range if applicable
      const timestamp = getColombiaDateString();
      let filename = `Remesas_${timestamp}`;
      if (filters.fecha_desde && filters.fecha_hasta) {
        filename = `Remesas_${filters.fecha_desde}_${filters.fecha_hasta}`;
      }

      // Download file
      XLSX.writeFile(wb, `${filename}.xlsx`);

      alert(`Se exportaron ${remesas.length} remesas a Excel`);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      alert("Error al exportar a Excel");
    }
  };

  const chipCls = (estado) => {
    const e = (estado || "").toLowerCase();
    if (e.includes("error")) return "bg-red-100 text-red-700";
    if (e.includes("validando")) return "bg-amber-100 text-amber-800";
    if (e.includes("validada")) return "bg-emerald-100 text-emerald-700";
    if (e.includes("asignada")) return "bg-violet-100 text-violet-700";
    if (e.includes("confirmada")) return "bg-orange-100 text-orange-700";
    if (e.includes("manifestada")) return "bg-gray-200 text-gray-700";
    return "bg-gray-100 text-gray-700";
  };


  return (
    <div className="px-2">
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
        <div className="flex items-end justify-between gap-4 p-4 border-b border-gray-100">
          <div>
            <h1 className="text-lg font-bold text-gray-900">
              Listado de Remesas
            </h1>
            <p className="text-sm text-gray-500">
              Gestiona y filtra tus remesas
            </p>
          </div>
          <button
            onClick={onCreateRemesa}
            className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg"
          >
            <Plus size={16} /> Nueva Remesa
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 p-4 border-b border-gray-100">
          <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
            <Search size={16} className="text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por número, cliente o conductor..."
              className="bg-transparent outline-none text-sm min-w-[220px]"
              value={filters.search}
              onChange={(e) => setFilters({...filters, search: e.target.value})}
            />
          </div>

          <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
            <Calendar size={16} className="text-gray-400" />
            <input
              type="date"
              placeholder="Desde"
              className="bg-transparent outline-none text-sm"
              value={filters.fecha_desde || ""}
              onChange={(e) =>
                setFilters({...filters, fecha_desde: e.target.value || null})
              }
            />
            {filters.fecha_desde && (
              <button
                onClick={() => setFilters({...filters, fecha_desde: null})}
                className="text-gray-400 hover:text-gray-600"
                title="Limpiar fecha desde"
              >
                <X size={14} />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
            <Calendar size={16} className="text-gray-400" />
            <input
              type="date"
              placeholder="Hasta"
              className="bg-transparent outline-none text-sm"
              value={filters.fecha_hasta || ""}
              onChange={(e) =>
                setFilters({...filters, fecha_hasta: e.target.value || null})
              }
            />
            {filters.fecha_hasta && (
              <button
                onClick={() => setFilters({...filters, fecha_hasta: null})}
                className="text-gray-400 hover:text-gray-600"
                title="Limpiar fecha hasta"
              >
                <X size={14} />
              </button>
            )}
          </div>

          <button
            onClick={exportToExcel}
            disabled={remesas.length === 0}
            className="inline-flex items-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            title="Exportar a Excel"
          >
            <Download size={16} /> Exportar Excel
          </button>

          <div className="ml-auto flex items-center gap-2">
            <select
              value={filters.estado || ""}
              onChange={(e) =>
                setFilters({...filters, estado: e.target.value || null})
              }
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white"
            >
              <option value="">Todos los estados</option>
              <option value="Recibida">Recibida</option>
              <option value="Validando">Validando</option>
              <option value="Error de Validación">Error de Validación</option>
              <option value="Validada">Validada</option>
              <option value="Asignada a Pre-Manifiesto">
                Asignada a Pre-Manifiesto
              </option>
              <option value="Confirmada para Carga">
                Confirmada para Carga
              </option>
              <option value="Manifestada">Manifestada</option>
            </select>
            <select
              value={filters.estado_asociacion || ""}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  estado_asociacion: e.target.value || null
                })
              }
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white"
            >
              <option value="">Todas las asociaciones</option>
              <option value="Sin Asignar">Sin Asignar</option>
              <option value="En Pre-Manifiesto">En Pre-Manifiesto</option>
              <option value="Manifestadas">Manifestadas</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : (
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-600">
                  <th className="text-left font-semibold px-4 py-3">
                    N° Remesa
                  </th>
                  <th className="text-left font-semibold px-4 py-3">Cliente</th>
                  <th className="text-left font-semibold px-4 py-3">
                    Conductor
                  </th>
                  <th className="text-left font-semibold px-4 py-3">Origen</th>
                  <th className="text-left font-semibold px-4 py-3">Destino</th>
                  <th className="text-left font-semibold px-4 py-3">Estado</th>
                  <th className="text-left font-semibold px-4 py-3">Fecha</th>
                  <th className="text-left font-semibold px-4 py-3 w-28">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {remesas.map((remesa, idx) => {
                  const hasError = remesa.estado === "Error de Validación";
                  const isRecibida = remesa.estado === "Recibida";
                  const fechaToCheck = remesa.fecha_cargue || remesa.fecha_creacion;
                  const vencido = isRecibida && isFechaVencidaColombia(fechaToCheck);
                  return (
                    <tr
                      key={remesa.remesa_id}
                      className={`${idx % 2 ? "bg-white" : "bg-gray-50"} ${
                        hasError ? "bg-red-50 border-l-4 border-red-500" : ""
                      } ${vencido ? "border-l-4 border-red-500" : ""}`}
                    >
                      <td className="px-4 py-3 font-mono font-semibold text-gray-900">
                        {hasError && (
                          <AlertTriangle
                            size={16}
                            className="inline-block mr-2 text-red-600"
                          />
                        )}
                        {remesa.numero_remesa}
                      </td>
                      <td className="px-4 py-3 text-gray-800">
                        {remesa.cliente || "N/A"}
                      </td>
                      <td className="px-4 py-3 text-gray-800">
                        {remesa.nombre_conductor || "N/A"}
                      </td>
                      <td className="px-4 py-3 text-gray-800">
                        {remesa.origen || "N/A"}
                      </td>
                      <td className="px-4 py-3 text-gray-800">
                        {remesa.destino || "N/A"}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full font-semibold ${chipCls(
                            remesa.estado
                          )}`}
                        >
                          {remesa.estado}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        <div className="flex items-center gap-2">
                          {formatDateOnlyForDisplay(remesa.fecha_cargue || remesa.fecha_creacion)}
                          {vencido && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold text-xs bg-red-500 text-white">
                              <AlertCircle size={12} /> Vencido
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => onRemesaSelect(remesa.remesa_id)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 hover:bg-gray-100"
                            title="Ver detalle"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => onEditRemesa(remesa.remesa_id)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 hover:bg-gray-100"
                            title="Editar"
                          >
                            <Edit size={16} />
                          </button>
                          {!remesa.pre_manifiesto_id && onPreManifiestoSelect && (
                            <button
                              onClick={(e) => handleCreatePreManifiestoFromRemesa(remesa, e)}
                              disabled={creatingPreManifiestoForRemesaId === remesa.remesa_id}
                              className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100"
                              title="Crear pre-manifiesto desde esta remesa"
                            >
                              <Truck size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RemesaList;

